/**
 * types.ts — local types for the Add-Building modal flow.
 *
 * Kept local rather than hoisted into `@/api` because these are *UI-shape*
 * types (step machine, form state) that don't cross the network boundary.
 * The one shape that is load-bearing across the boundary is `SignerRelationship`
 * — the enum values must match the backend's `AttestationVerificationSnapshot`
 * contract (see `docs/compliance/attestation_verification_design.md` §1 Q2).
 */

import type { FilingContext } from "@/api/filingContext";

// ---------------------------------------------------------------------------
// SignerRelationship — backend-owned enum values, locked in by Leo.
// Do NOT drift. If you need a new value, add it here first, then align
// backend. The labels below are UI-only copy.
// ---------------------------------------------------------------------------

export type SignerRelationship =
  | "OWNER"
  | "OFFICER"
  | "MANAGING_AGENT"
  | "AUTHORIZED_REPRESENTATIVE"
  | "OTHER";

export interface SignerRelationshipOption {
  value: SignerRelationship;
  label: string;
  /** Requires a supporting doc upload in Step 4. */
  requiresSupportingDoc: boolean;
  /** Requires a free-text justification in Step 4. */
  requiresJustification: boolean;
}

export const SIGNER_RELATIONSHIP_OPTIONS: SignerRelationshipOption[] = [
  {
    value: "OWNER",
    label: "I am the owner",
    requiresSupportingDoc: false,
    requiresJustification: false,
  },
  {
    value: "OFFICER",
    label: "I am a corporate officer of the owner",
    requiresSupportingDoc: false,
    requiresJustification: false,
  },
  {
    value: "MANAGING_AGENT",
    label: "I am the managing agent",
    requiresSupportingDoc: false,
    requiresJustification: false,
  },
  {
    value: "AUTHORIZED_REPRESENTATIVE",
    label: "I am authorized by the owner to sign",
    requiresSupportingDoc: true,
    requiresJustification: false,
  },
  {
    value: "OTHER",
    label: "Other",
    requiresSupportingDoc: true,
    requiresJustification: true,
  },
];

// ---------------------------------------------------------------------------
// Step machine
// ---------------------------------------------------------------------------

export type AddBuildingStepId =
  | "identify"
  | "verifyCbl"
  | "connectData"
  | "confirmOwner";

export const STEP_ORDER: AddBuildingStepId[] = [
  "identify",
  "verifyCbl",
  "connectData",
  "confirmOwner",
];

export interface AddBuildingStepMeta {
  id: AddBuildingStepId;
  title: string;
  shortTitle: string;
}

export const STEP_META: Record<AddBuildingStepId, AddBuildingStepMeta> = {
  identify: {
    id: "identify",
    title: "Find your building",
    shortTitle: "Identify",
  },
  verifyCbl: {
    id: "verifyCbl",
    title: "Verify with NYC",
    shortTitle: "Verify CBL",
  },
  connectData: {
    id: "connectData",
    title: "Connect your data",
    shortTitle: "Connect data",
  },
  confirmOwner: {
    id: "confirmOwner",
    title: "Confirm the owner",
    shortTitle: "Confirm owner",
  },
};

// ---------------------------------------------------------------------------
// CBL preview outcome — mirrors the (in-flight) backend preview endpoint.
// Three mutually-exclusive states. Falls back to the existing /cbl_check/
// response when the preview endpoint isn't ready (see useCblPreview.ts).
// ---------------------------------------------------------------------------

export type CblPreviewOutcome = "on_2026_cbl" | "delisted" | "never_on_cbl";

/**
 * Minimal identity for a matched building. All three are optional at the FE
 * type level so we tolerate degraded inputs (address-only, BIN-only, etc.);
 * backend always returns at least address + one of bin/bbl when it has a
 * row, and neither + the typed search term for `never_on_cbl`.
 */
export interface CblIdentity {
  address: string | null;
  bin: string | null;
  bbl: string | null;
  /** HPD-registered primary owner entity, when available. Used for the
   * Step-4 inline mismatch warning; null when HPD has no data. */
  hpdOwnerName: string | null;
  /**
   * Humanized building class string ("Apartments", "Office", "Mixed-use", …).
   * Backend humanizes raw 2-char PLUTO codes (`bldgclass`) at the API
   * boundary — see Sprint 7 §F1 spec. Nullable for back-compat with the
   * pre-Sprint-7 API and for legacy `/cbl_check/` fallback rows.
   */
  buildingClass: string | null;
  /**
   * 4-digit year the structure was built (PLUTO `yearbuilt`). Nullable
   * because not every PLUTO row has it and the legacy fallback can't
   * supply it.
   */
  yearBuilt: number | null;
}

export interface CblPreviewMatch {
  identity: CblIdentity;
  outcome: CblPreviewOutcome;
  /** Populated for `on_2026_cbl`. One entry per applicable law (LL97, LL88). */
  filingContexts: FilingContext[];
  /**
   * When non-null, this candidate is already onboarded by the current user's
   * company. The IdentifyStep row replaces "select" with a route to the
   * existing building (Sprint 7 §F3) — never POST a duplicate.
   *
   * Computed server-side and scoped to `request.user.company`; null when
   * the request is unauthenticated or the candidate isn't tracked.
   */
  alreadyTrackedBuildingId: string | null;
}

export interface CblPreviewResponse {
  /** Multiple matches happen on ambiguous BBL lookups. Length 0 is how the
   * FE represents `never_on_cbl`; length 1 with `outcome === "delisted"`
   * is the delisted branch; length 1+ with `outcome === "on_2026_cbl"` is
   * the happy path. */
  matches: CblPreviewMatch[];
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

export interface AddBuildingIdentifyForm {
  /** Single combined input per Leo's locked-in design. Parsed as BBL if 10
   * digits, BIN if 7 digits, else address. Controlled-input only — does
   * NOT trigger a network fetch on change. */
  searchTerm: string;
  /** The term the user explicitly submitted (Search button / Enter).
   * Empty string = no search yet → no fetch. Decoupled from searchTerm
   * so the user can edit freely without firing fetches mid-typing. Set
   * to the trimmed searchTerm on SUBMIT_SEARCH; cleared back to "" on
   * RESET. SET_SEARCH_TERM leaves submittedTerm alone so prior results
   * stay visible while the user edits — they re-submit to refresh. */
  submittedTerm: string;
}

export interface AddBuildingEspmSelection {
  propertyId: string;
  buildingName: string;
  address: string;
}

export interface AddBuildingConfirmOwnerForm {
  legalOwnerEntity: string;
  signerRelationship: SignerRelationship | null;
  /** Required when signerRelationship === "OTHER". Min 50 chars. */
  justification: string;
  /** Required when signerRelationship === "AUTHORIZED_REPRESENTATIVE" or
   * "OTHER". Max one file. */
  supportingDoc: File | null;
}

/**
 * Full modal draft. Lives in a single useReducer slot inside
 * `AddBuildingDialog`. Steps never mutate draft shape shared across steps
 * — they always go through dispatch actions in `AddBuildingDialog`.
 *
 * `wasAmbiguous` / `originalCandidateCount` are F2-plumbing for D1's recovery
 * banner: D1 shows "You picked X from N buildings at this BBL" iff the user
 * picked from a multi-candidate set. Set on `SELECT_MATCH` from the
 * candidate-count carried by the action; defaults to false / 0.
 */
export interface AddBuildingDraft {
  step: AddBuildingStepId;
  identify: AddBuildingIdentifyForm;
  selectedMatch: CblPreviewMatch | null;
  /** True when the user (or auto-advance) picked from a list of >1 matches. */
  wasAmbiguous: boolean;
  /** Number of candidates visible at the time the user picked. Used by D1
   * banner copy. 0 before any pick. */
  originalCandidateCount: number;
  espmSelection: AddBuildingEspmSelection | null;
  espmSkipped: boolean;
  confirmOwner: AddBuildingConfirmOwnerForm;
}

export const INITIAL_DRAFT: AddBuildingDraft = {
  step: "identify",
  identify: { searchTerm: "", submittedTerm: "" },
  selectedMatch: null,
  wasAmbiguous: false,
  originalCandidateCount: 0,
  espmSelection: null,
  espmSkipped: false,
  confirmOwner: {
    legalOwnerEntity: "",
    signerRelationship: null,
    justification: "",
    supportingDoc: null,
  },
};
